import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box
} from '@mui/material';
import { createPerson, updatePerson } from '../services/api/personManagementService';
import { getCapturedImageDisplayUrl } from '../services/s3/captureUrlService';
import {
  CHARACTERISTICS_METADATA
} from '@gamma/shared/attributes';
import AttributeSelectorGroup from '../components/AttributeSelectorGroup';
import DatasetGroupSelector from '../components/DatasetGroupSelector';
import InterestLevelSelector from '../components/InterestLevelSelector';
import { GenerateCharacteristics } from '../appcomponents/characteristics/CharacteristicsGenerator';
import { useSearch } from '../contexts/searchContext';
import { config } from '../config/env';

/**
 * PersonDialog - Create new person or edit existing person
 * Mobile-first compact design
 */
export default function PersonDialog({ open, person, onClose, onSaved }) {
  const isEditMode = !!person;

  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Get datasets from context
  const { datasetObjects } = useSearch();

  // Get available characteristics schema
  const characteristicsSchema = GenerateCharacteristics(null);

  // Filter datasets that have groups
  const datasetsWithGroups = useMemo(() => {
    return datasetObjects.filter(ds =>
      ds.metadata?.groups && Array.isArray(ds.metadata.groups) && ds.metadata.groups.length > 0
    );
  }, [datasetObjects]);

  // Handle characteristic attribute changes
  function handleCharacteristicChange(attributeType, newValue) {
    const metadata = CHARACTERISTICS_METADATA[attributeType];
    if (metadata) {
      handleChange(metadata.dbField, newValue);
    }
  }

  // Get current attribute values from formData
  function getAttributeValues() {
    const values = {};
    Object.entries(CHARACTERISTICS_METADATA).forEach(([attributeType, metadata]) => {
      values[attributeType] = formData[metadata.dbField];
    });
    return values;
  }

  useEffect(() => {
    if (person) {
      // Edit mode - populate with existing data
      console.log('[PersonDialog] Opening in edit mode:', {
        person_id: person.person_id,
        dataset: person.dataset,
        group: person.group,
        first_name: person.first_name,
        last_name: person.last_name
      });
      setFormData({
        person_id: person.person_id || '',
        dataset: person.dataset || '',
        first_name: person.first_name || '',
        last_name: person.last_name || '',
        persontype: person.persontype || '',
        group: person.group || '',
        headline: person.headline || '',
        image: person.image || '',
        // Unified schema: read from physical_attributes
        gender: person.physical_attributes?.gender || '',
        agegroup: person.physical_attributes?.age_group || '',
        facialhair: person.physical_attributes?.facial_hair || '',
        glasses: person.physical_attributes?.glasses || '',
        // Legacy flat fields (not in unified schema)
        height: person.height || '',
        build: person.build || '',
        skintone: person.skintone || '',
        eyecolor: person.eyecolor || '',
        haircolor: person.haircolor || '',
        hairstyle: person.hairstyle || '',
        dress: person.dress || '',
        // Unified schema: read from interest object
        interest_level: person.interest?.level || '',
        interest_reason: person.interest?.reason || '',
        uri: person.uri || ''
      });
      setError(null);
    } else {
      // Create mode - empty form
      resetForm();
    }
  }, [person, open]);

  function resetForm() {
    setFormData({
      person_id: '',
      dataset: '',
      first_name: '',
      last_name: '',
      persontype: '',
      group: '',
      headline: '',
      image: '',
      gender: '',
      agegroup: '',
      height: '',
      build: '',
      skintone: '',
      eyecolor: '',
      haircolor: '',
      hairstyle: '',
      facialhair: '',
      glasses: '',
      dress: '',
      interest_level: '',
      interest_reason: '',
      uri: ''
    });
    setError(null);
  }

  function handleChange(field, value) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    // Validation
    if (!formData.first_name?.trim() || !formData.last_name?.trim()) {
      setError('First name and last name are required');
      return;
    }

    if (!isEditMode && !formData.person_id?.trim()) {
      setError('Person ID is required');
      return;
    }

    if (!formData.dataset?.trim()) {
      setError('Dataset is required');
      return;
    }

    // Validate person_id format (create mode only)
    if (!isEditMode && !/^[A-Za-z0-9_-]+$/.test(formData.person_id)) {
      setError('Person ID suffix must contain only letters, numbers, hyphens, and underscores');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (isEditMode) {
        // Update existing person
        const { person_id, ...updates } = formData;
        await updatePerson(person.person_id, updates);
      } else {
        // Create new person - construct full person_id with dataset prefix
        const fullPersonId = `${formData.dataset}-${formData.person_id}`;
        const personData = {
          ...formData,
          person_id: fullPersonId
        };
        await createPerson(personData);
      }

      onSaved(formData);
      if (!isEditMode) resetForm();
    } catch (err) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} person:`, err);
      setError(err.message || `Failed to ${isEditMode ? 'update' : 'create'} person`);
    } finally {
      setSaving(false);
    }
  }

  function handleClose() {
    if (!isEditMode) resetForm();
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '92vh', maxWidth: '720px' } }}
    >
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Grid container spacing={1.2}>
          {!isEditMode && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Person ID Suffix"
                value={formData.person_id || ''}
                onChange={(e) => handleChange('person_id', e.target.value.toUpperCase())}
                required
                size="small"
                placeholder="e.g., 001, SMITH"
                helperText={
                  formData.dataset && formData.person_id
                    ? `Full ID: ${formData.dataset}-${formData.person_id}`
                    : 'Enter suffix (will be prefixed with dataset ID)'
                }
                inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={formData.first_name || ''}
              onChange={(e) => handleChange('first_name', e.target.value)}
              required
              size="small"
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={formData.last_name || ''}
              onChange={(e) => handleChange('last_name', e.target.value)}
              required
              size="small"
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
            />
          </Grid>


          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Headline"
              value={formData.headline || ''}
              onChange={(e) => handleChange('headline', e.target.value)}
              size="small"
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Image URL"
              value={formData.image || ''}
              onChange={(e) => handleChange('image', e.target.value)}
              size="small"
              placeholder="https://example.com/image.jpg"
              helperText="Image will be downloaded and indexed"
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
            />
          </Grid>

          {/* Characteristics Section */}
          <Grid item xs={12} sx={{ mt: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Person Image - always show placeholder */}
              <Box
                sx={{
                  flexShrink: 0,
                  width: 280,
                  height: 280
                }}
              >
                {formData.image ? (
                  <Box
                    component="img"
                    src={formData.image.startsWith('http') ? formData.image : getCapturedImageDisplayUrl(formData.image)}
                    alt={formData.first_name ? `${formData.first_name} ${formData.last_name}` : 'Person preview'}
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: 1,
                      border: '2px dashed',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'background.default'
                    }}
                  >
                    <Typography variant="body2" color="text.disabled">
                      No image
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Attribute Selectors */}
              <Box
                sx={{
                  flex: 1,
                  height: 280,
                  overflowY: 'auto',
                  pr: 1,
                  py: 1,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    bgcolor: 'grey.100',
                    borderRadius: 1
                  },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'grey.400',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'grey.600'
                    }
                  }
                }}
              >
                {/* Group Selection */}
                <DatasetGroupSelector
                  datasets={datasetsWithGroups}
                  selectedDatasetId={formData.dataset || null}
                  selectedGroup={formData.group || null}
                  onChange={(datasetId, groupCode) => {
                    setFormData(prev => ({
                      ...prev,
                      dataset: datasetId || '',
                      group: groupCode || ''
                    }));
                  }}
                  iconSize={35}
                  showLabel={false}
                />

                {/* Interest Level */}
                <InterestLevelSelector
                  selectedLevel={formData.interest_level || null}
                  onChange={(level) => handleChange('interest_level', level || '')}
                  iconSize={35}
                  showLabel={false}
                  allowDeselect={true}
                />

                {/* Physical Characteristics */}
                <AttributeSelectorGroup
                  attributeValues={getAttributeValues()}
                  onChange={handleCharacteristicChange}
                  availableAttributes={characteristicsSchema}
                  iconSize={35}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Interest Reason"
              value={formData.interest_reason || ''}
              onChange={(e) => handleChange('interest_reason', e.target.value)}
              size="small"
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="URI"
              value={formData.uri || ''}
              onChange={(e) => handleChange('uri', e.target.value)}
              size="small"
              placeholder="https://example.com/person/..."
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        {error && (
          <Typography variant="caption" color="error" sx={{ mr: 'auto', fontSize: '0.75rem' }}>
            {error}
          </Typography>
        )}
        <Button onClick={handleClose} size="small">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          size="small"
        >
          {saving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
